import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';
import { LineAllFieldsFragment } from '../../../../generated/graphql';
import {
  resetMapRouteEditorStateAction,
  selectIsTimingSettingsModalOpen,
  selectIsViaModalOpen,
  selectLastCreatedRouteLabel,
  setLastCreatedRouteLabelAction,
  setLineInfoAction,
  startRouteCreatingAction,
  useAppDispatch,
  useAppSelector,
} from '../../../../redux';
import { Priority } from '../../../../types/enums';
import { getTransportModeIcon, isPastEntity, none } from '../../../../utils';
import { today } from '../../../../utils/time';
import {
  Column,
  Container,
  Row,
  Visible,
} from '../../../common/LayoutComponents';
import { useNavigateToMap } from '../../../common/Utils';
import {
  LineFetchError,
  LineTitle,
  useGetLineDetails,
  useGetRoutesDisplayedInList,
} from '../../Common';
import { LineLatestChanges } from '../ChangeHistory/Components/LatestChangeHistory/LineLatestChanges';
import { TimingSettingsModal } from '../StopTimingSettings/TimingSettingsModal';
import { ViaModal } from '../Via/ViaModal';
import { ActionsRow } from './ActionsRow';
import { AdditionalInformation } from './AdditionalInformation';
import { CreateRouteBox } from './CreateRouteBox';
import { LineDetailsEmptyMapPlaceholder } from './LineDetailsEmptyMapPlaceholder';
import { LineMissingBox } from './LineMissingBox';
import { LineRouteList } from './LineRouteList';

export const LineDetailsByIdPage: FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigateToMap = useNavigateToMap();

  const { line, lineError } = useGetLineDetails();

  const { displayedRouteLabels, setDisplayedRoutesToUrl } =
    useGetRoutesDisplayedInList(line);

  const lastCreatedRouteLabel = useAppSelector(selectLastCreatedRouteLabel);

  // When returning from the map after creating a route, add the new route to
  // the displayed-routes selection (append) once it is present in the line's
  // routes, then clear the one-time flag. Waiting for the route to appear is
  // needed because the line query first returns stale cache (cache-and-network).
  useEffect(() => {
    if (!lastCreatedRouteLabel || !line) {
      return;
    }

    if (
      none((route) => route.label === lastCreatedRouteLabel, line.line_routes)
    ) {
      return;
    }

    if (!displayedRouteLabels.includes(lastCreatedRouteLabel)) {
      setDisplayedRoutesToUrl([...displayedRouteLabels, lastCreatedRouteLabel]);
    }

    dispatch(setLastCreatedRouteLabelAction(undefined));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [lastCreatedRouteLabel, line, displayedRouteLabels]);

  const createRoute = (routeLine: LineAllFieldsFragment) => {
    dispatch(resetMapRouteEditorStateAction());
    dispatch(setLineInfoAction(routeLine));
    dispatch(startRouteCreatingAction(routeLine.primary_vehicle_mode));
    navigateToMap();
  };

  const isViaModalOpen = useAppSelector(selectIsViaModalOpen);
  const isTimingSettingsModalOpen = useAppSelector(
    selectIsTimingSettingsModalOpen,
  );

  const isRouteCreationAllowed = line && !isPastEntity(today(), line);
  const onCreateRoute = isRouteCreationAllowed
    ? () => createRoute(line)
    : undefined;

  const displayedRoutes =
    line?.line_routes?.filter((route) =>
      displayedRouteLabels?.includes(route.label),
    ) ?? [];

  const isDraft = line?.priority === Priority.Draft;

  return (
    <div>
      <div
        className={twMerge(
          'border-b border-light-grey bg-background',
          // Remove border because draft-divider handles separation in draft mode
          isDraft ? 'border-0' : '',
        )}
      >
        <Container>
          <Row>
            {line?.primary_vehicle_mode && (
              <i
                className={twJoin(
                  getTransportModeIcon(
                    line.primary_vehicle_mode,
                    line.type_of_line,
                  ),
                  'mx-2 mt-2 text-6xl',
                )}
              />
            )}
            {line && <LineTitle line={line} onCreateRoute={onCreateRoute} />}
          </Row>
        </Container>
      </div>
      {isDraft && <div className="draft-divider" />}
      <ActionsRow className="pt-4 pb-0" />
      <Container className="pt-10">
        {line ? (
          <>
            <Row>
              <AdditionalInformation className="w-2/4" line={line} />
              <LineLatestChanges className="w-2/4" label={line.label} />
            </Row>
            <Row>
              <Column className="w-full">
                <h2 className="mt-8">{t(($) => $.lines.routes)}</h2>
                {line.line_routes?.length > 0 ? (
                  <LineRouteList routes={displayedRoutes} />
                ) : (
                  <CreateRouteBox onCreateRoute={onCreateRoute} />
                )}
              </Column>
            </Row>
          </>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            <div className="col col-span-3">
              <LineMissingBox
                error={lineError ?? LineFetchError.LINE_MISSING_DEFAULT}
              />
            </div>
            <div className="col col-span-1">
              <LineDetailsEmptyMapPlaceholder />
            </div>
          </div>
        )}
      </Container>
      <Visible visible={isViaModalOpen}>
        <ViaModal />
      </Visible>
      <Visible visible={isTimingSettingsModalOpen}>
        <TimingSettingsModal />
      </Visible>
    </div>
  );
};
